<template>
  <div
    class="currency-seller">
    <p class="currency-seller__nickname">
      {{ user.username }}
    </p>

    <div class="currency-seller__picture-container">
      <base-tooltip
        :triggers="tooltipTriggers"
        :z-index="1051"
        type="info-gray">
        <template #default>
          <img
            class="currency-seller__avatar"
            :src="userAvatarPath"
            alt="Seller avatar">
        </template>
        <template #content>
          <online-text
            :user-id="user.id"
            :is-online="user.is_online"
            :was-online-at="user.was_online_at" />
        </template>
      </base-tooltip>

      <online-indicator
        :is-online="user.is_online"
        :user-id="user.id"
        class="currency-seller__online-indicator" />
    </div>

    <div class="currency-seller__review-wrapper">
      <span class="currency-seller__rating-wrapper">
        <span
          class="currency-seller__star"
          :class="`currency-seller__star--${reviewsData.icon}`" />
        <span
          v-if="reviewsData.rating"
          class="currency-seller__rating-text">
          {{ reviewsData.rating }}
        </span>
      </span>

      <span
        class="currency-seller__reviews-count transition--color"
        :class="{ 'currency-seller__reviews-count--light': !reviewsData.rating }">
        {{ reviewsData.reviews }}
      </span>
    </div>

    <online-text
      :user-id="user.id"
      :is-online="user.is_online"
      :was-online-at="user.was_online_at"
      class="currency-seller__activity" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import BaseTooltip from 'Components/ui/Tooltip/BaseTooltip.vue';
import OnlineIndicator from 'Components/ui/Online/OnlineIndicator.vue';
import OnlineText from 'Components/ui/Online/OnlineText.vue';

export default {
  name: 'CurrencySeller',

  components: {
    OnlineText,
    OnlineIndicator,
    BaseTooltip,
  },

  props: {
    user: {
      type: Object,
      default: () => ({
        username: 'SkyCoach',
        avatar: null,
        rating: 5,
        marketplace_rating_count: 5,
        is_online: true,
        was_online_at: null,
      }),
    },
  },

  computed: {
    ...mapState(['userDevice']),

    userAvatarPath() {
      return this.user.avatar ? `/storage${this.user.avatar}` : '/images/interface/default-seller-avatar.jpg';
    },

    tooltipTriggers() {
      return this.userDevice.innerWidth > 1024 ? ['hover'] : [];
    },

    reviewsData() {
      const hasRating = !!this.user.marketplace_rating;

      let rating;
      let reviews;
      let icon;

      if (!hasRating) {
        reviews = 'New Seller';
        icon = 'grey';
      } else {
        rating = this.user.marketplace_rating.toFixed(1);
        reviews = this.user.marketplace_rating_count ? `${this.user.marketplace_rating_count} reviews` : '';
        icon = 'yellow';
      }

      return {
        rating,
        reviews,
        icon,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
  .currency-seller {
    display: grid;
    grid-template-areas:"nickname nickname"
                        "avatar review"
                        "avatar activity";
    grid-template-rows: auto 1fr 1fr;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 0 16px;
  }

  .currency-seller__nickname {
    font-weight: 500;
    font-size: 13px;
    grid-area: nickname;
    margin-bottom: 7px;
  }

  .currency-seller__picture-container {
    grid-area: avatar;
    position: relative;
    display: flex;
  }

  .currency-seller__avatar {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 50%;
    background-color: $color--basic-menu;
  }

  .currency-seller__online-indicator {
    position: absolute;
    width: 8px;
    height: 8px;
    top: 3px;
    right: 2px;
  }

  .currency-seller__review-wrapper {
    margin-top: -5px;
    grid-area: review;
    display: flex;
    align-items: center;
  }

  .currency-seller__rating-wrapper {
    font-size: 13px;
    display: flex;
    align-items: center;
  }

  .currency-seller__rating-text {
    margin-right: 8px;
  }

  .currency-seller__star {
    display: inline-block;
    -webkit-mask-image: url(/images/icons/star-stroke.svg);
    mask-image: url(/images/icons/star-stroke.svg);
    -webkit-mask-size: 16px 16px;
    mask-size: 16px 16px;
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }

  .currency-seller__star--yellow {
    background: $color--basic-yellow;
  }

  .currency-seller__star--grey {
    background: $color--basic-dark-gray;
  }

  .currency-seller__activity {
    grid-area: activity;
    color: $color--basic-white;
    font-size: 13px;
  }

  .currency-seller__reviews-count {
    font-size: 13px;
    color: $color--basic-dark-gray;
  }

  .currency-seller__reviews-count--light {
    color: $color--basic-light-gray;
  }

  @media ($screen-more-or-1024) {
    .currency-seller {
      grid-template-areas:"avatar nickname"
                          "avatar review";
      grid-template-rows: auto 1fr;
      grid-template-columns: auto 1fr;
      gap: 0 20px;
    }

    .currency-seller__review-wrapper {
      margin-top: 0;
    }

    .currency-seller__activity {
      display: none;
    }

    .currency-seller__avatar {
      width: 48px;
      height: 48px;
    }

    .currency-seller__nickname {
      margin-top: 2px;
      margin-bottom: 4px;
    }

    .currency-seller__nickname,
    .currency-seller__reviews-count,
    .currency-seller__rating-wrapper {
      font-size: 15px;
    }
  }
</style>
